import { z } from 'zod';

// Date schema for API date strings
export const ApiDateSchema = z.string().transform((val) => new Date(val));

// Common Response Schema
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.any().optional(),
  error: z.any().optional(),
});

// AUTH TYPES
export const LoginPasswordRequestSchema = z.object({
  username: z.string(),
  password: z.string(),
  remember_me: z.boolean().optional(),
});

export const LoginSendCodeRequestSchema = z.object({
  username: z.string(),
});

export const LoginVerifyCodeRequestSchema = z.object({
  username: z.string(),
  code: z.string(),
  remember_me: z.boolean().optional(),
});

export const UserProfileSchema = z.object({
  address: z.string(),
  photo: z.string().url().nullish(),
  date_of_birth: z.string().nullish(),
  membership_id: z.string().nullish(),
  finance_name: z.string(),
});

export const UserSchema = z.object({
  id: z.number(),
  username: z.string(),
  first_name: z.string(),
  last_name: z.string().optional(),
  date_joined: ApiDateSchema,
  role: z.string(),
  profile: UserProfileSchema,
});

export const LoginResponseSchema = z.object({
  user: UserSchema,
  access: z.string(),
  refresh: z.string(),
  access_expiration: ApiDateSchema,
  refresh_expiration: ApiDateSchema,
});

// MEMBER TYPES
export const MemberSchema = UserSchema;
export const MemberListResponseSchema = z.array(MemberSchema);

export const CreateMemberRequestSchema = z.object({
  username: z.string(),
  first_name: z.string(),
  last_name: z.string().optional(),
  password: z.string().optional(),
  role: z.string(),
  profile: UserProfileSchema.optional(),
});

export const UpdateMemberRequestSchema = CreateMemberRequestSchema.partial();

// Role schema
export const RoleSchema = z.object({
  id: z.number(),
  role: z.string(),
});

export const RoleListResponseSchema = z.array(RoleSchema);

// Define types from schemas
export type ApiResponse<T = any> = z.infer<typeof ApiResponseSchema> & {
  data?: T;
};
export type LoginPasswordRequest = z.infer<typeof LoginPasswordRequestSchema>;
export type LoginSendCodeRequest = z.infer<typeof LoginSendCodeRequestSchema>;
export type LoginVerifyCodeRequest = z.infer<
  typeof LoginVerifyCodeRequestSchema
>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
export type User = z.infer<typeof UserSchema>;
export type Member = z.infer<typeof MemberSchema>;
export type CreateMemberRequest = z.infer<typeof CreateMemberRequestSchema>;
export type UpdateMemberRequest = z.infer<typeof UpdateMemberRequestSchema>;
export type Role = z.infer<typeof RoleSchema>;

// Error Types
export interface ApiError {
  message: string;
  code?: number;
  details?: any;
  type?: string;
}
